import { apiDownload, apiFetch, setToken } from "./client";
import type {
  ApiKey,
  AppUser,
  AuthResponse,
  BrandingDto,
  BrandingRequest,
  EmailConfigDto,
  EmailConfigRequest,
  ExternalEndpointConfig,
  FieldMapping,
  IssuedLicense,
  LicenseOverview,
  SetupStatus,
  ManualPaymentRequest,
  MpesaConfigDto,
  MpesaConfigRequest,
  Page,
  PaymentTransaction,
  Plan,
  PlatformStats,
  RevenueOverview,
  ReportDefinition,
  ReportExecutionLog,
  ReportFolder,
  ReportParamDefinition,
  ReportSchedule,
  ReportScheduleRequest,
  ReportScheduleRun,
  ReportTemplateVersion,
  ResetPasswordResponse,
  SelfHostLicense,
  SubscribeRequest,
  SubscribeResponse,
  Subscription,
  Tenant,
  TenantDetail,
  TenantOnboardingResponse,
  TenantUsage,
  UserInviteResponse,
  TemplateFragment,
  TemplateSnippet,
} from "./types";

export const adminApi = {
  // On-premise installs: public status + first-run setup, and license management afterwards.
  setup: {
    status: () => apiFetch<SetupStatus>("/v1/setup/status", { skipAuth: true }),
    initialize: async (body: {
      licenseKey?: string;
      organisationName: string;
      adminName?: string;
      adminEmail: string;
      adminPassword: string;
    }) => {
      const res = await apiFetch<AuthResponse>("/v1/setup/initialize", { method: "POST", body, skipAuth: true });
      setToken(res.token);
      return res;
    },
  },

  account: {
    changePassword: (currentPassword: string, newPassword: string) =>
      apiFetch<void>("/v1/account/password", { method: "POST", body: { currentPassword, newPassword } }),
  },

  license: {
    overview: () => apiFetch<LicenseOverview>("/v1/license"),
    install: (licenseKey: string) => apiFetch<LicenseOverview>("/v1/license", { method: "POST", body: { licenseKey } }),
  },

  auth: {
    login: async (email: string, password: string, tenantSlug?: string) => {
      const res = await apiFetch<AuthResponse>("/v1/auth/login", {
        method: "POST",
        body: { email, password, tenantSlug },
        skipAuth: true,
      });
      setToken(res.token);
      return res;
    },
    signup: async (body: {
      companyName: string;
      slug: string;
      ownerFullName?: string;
      ownerEmail: string;
      password: string;
    }) => {
      const res = await apiFetch<AuthResponse>("/v1/auth/signup", {
        method: "POST",
        body,
        skipAuth: true,
      });
      setToken(res.token);
      return res;
    },
    logout: async () => {
      await apiFetch<void>("/v1/auth/logout", { method: "POST" }).catch(() => undefined);
      setToken(null);
    },
  },

  tenants: {
    list: () => apiFetch<Tenant[]>("/v1/admin/tenants"),
    create: (body: {
      name: string;
      slug: string;
      planTier?: string;
      ownerEmail: string;
      ownerFullName?: string;
    }) => apiFetch<TenantOnboardingResponse>("/v1/admin/tenants", { method: "POST", body }),
    suspend: (id: string) =>
      apiFetch<Tenant>(`/v1/admin/tenants/${id}/suspend`, { method: "POST" }),
    activate: (id: string) =>
      apiFetch<Tenant>(`/v1/admin/tenants/${id}/activate`, { method: "POST" }),
    get: (id: string) => apiFetch<TenantDetail>(`/v1/admin/tenants/${id}`),
    updatePlan: (id: string, planTier: string) =>
      apiFetch<Tenant>(`/v1/admin/tenants/${id}/plan`, { method: "PUT", body: { planTier } }),
    resetOwnerPassword: (id: string) =>
      apiFetch<ResetPasswordResponse>(`/v1/admin/tenants/${id}/reset-owner-password`, { method: "POST" }),
  },

  users: {
    list: () => apiFetch<AppUser[]>("/v1/admin/users"),
    invite: (email: string, fullName: string, roles: string[]) =>
      apiFetch<UserInviteResponse>("/v1/admin/users", { method: "POST", body: { email, fullName, roles } }),
    setRoles: (userId: string, roles: string[]) =>
      apiFetch<AppUser>(`/v1/admin/users/${userId}/roles`, { method: "PUT", body: { roles } }),
    disable: (userId: string) =>
      apiFetch<AppUser>(`/v1/admin/users/${userId}/disable`, { method: "POST" }),
    enable: (userId: string) =>
      apiFetch<AppUser>(`/v1/admin/users/${userId}/enable`, { method: "POST" }),
    resetPassword: (userId: string) =>
      apiFetch<ResetPasswordResponse>(`/v1/admin/users/${userId}/reset-password`, { method: "POST" }),
  },

  apiKeys: {
    list: () => apiFetch<ApiKey[]>("/v1/admin/api-keys"),
    create: (label: string, scopes: string[]) =>
      apiFetch<ApiKey>("/v1/admin/api-keys", { method: "POST", body: { label, scopes } }),
    revoke: (id: string) => apiFetch<void>(`/v1/admin/api-keys/${id}`, { method: "DELETE" }),
  },

  fragments: {
    list: () => apiFetch<TemplateFragment[]>("/v1/admin/template-fragments"),
    create: (body: Omit<TemplateFragment, "id">) =>
      apiFetch<TemplateFragment>("/v1/admin/template-fragments", { method: "POST", body }),
    update: (id: string, body: Partial<TemplateFragment>) =>
      apiFetch<TemplateFragment>(`/v1/admin/template-fragments/${id}`, { method: "PUT", body }),
    remove: (id: string) => apiFetch<void>(`/v1/admin/template-fragments/${id}`, { method: "DELETE" }),
  },

  folders: {
    list: () => apiFetch<ReportFolder[]>("/v1/admin/folders"),
    create: (body: Partial<ReportFolder> & { name: string }) =>
      apiFetch<ReportFolder>("/v1/admin/folders", { method: "POST", body }),
    update: (id: string, body: Partial<ReportFolder>) =>
      apiFetch<ReportFolder>(`/v1/admin/folders/${id}`, { method: "PUT", body }),
    remove: (id: string) => apiFetch<void>(`/v1/admin/folders/${id}`, { method: "DELETE" }),
  },

  snippets: {
    list: () => apiFetch<TemplateSnippet[]>("/v1/admin/snippets"),
    create: (body: { name: string; htmlContent: string }) =>
      apiFetch<TemplateSnippet>("/v1/admin/snippets", { method: "POST", body }),
    remove: (id: string) => apiFetch<void>(`/v1/admin/snippets/${id}`, { method: "DELETE" }),
  },

  reports: {
    list: () => apiFetch<ReportDefinition[]>("/v1/admin/reports"),
    get: (id: string) => apiFetch<ReportDefinition>(`/v1/admin/reports/${id}`),
    create: (body: Partial<ReportDefinition> & { reportKey: string; displayName: string }) =>
      apiFetch<ReportDefinition>("/v1/admin/reports", { method: "POST", body }),
    update: (id: string, body: Partial<ReportDefinition>) =>
      apiFetch<ReportDefinition>(`/v1/admin/reports/${id}`, { method: "PUT", body }),
    remove: (id: string) => apiFetch<void>(`/v1/admin/reports/${id}`, { method: "DELETE" }),

    getParams: (reportId: string) =>
      apiFetch<ReportParamDefinition[]>(`/v1/admin/reports/${reportId}/params`),
    saveParams: (reportId: string, params: ReportParamDefinition[]) =>
      apiFetch<ReportParamDefinition[]>(`/v1/admin/reports/${reportId}/params`, {
        method: "PUT",
        body: { params },
      }),

    getMapping: (reportId: string) =>
      apiFetch<FieldMapping[]>(`/v1/admin/reports/${reportId}/mapping`),
    saveMapping: (reportId: string, mappings: FieldMapping[]) =>
      apiFetch<FieldMapping[]>(`/v1/admin/reports/${reportId}/mapping`, {
        method: "PUT",
        body: { mappings },
      }),
    fetchSampleData: (reportId: string) =>
      apiFetch<unknown>(`/v1/admin/reports/${reportId}/mapping/sample-data`, { method: "POST" }),

    getEndpoint: (reportId: string) =>
      apiFetch<ExternalEndpointConfig | null>(`/v1/admin/reports/${reportId}/endpoint`),
    saveEndpoint: (reportId: string, body: ExternalEndpointConfig) =>
      apiFetch<ExternalEndpointConfig>(`/v1/admin/reports/${reportId}/endpoint`, {
        method: "PUT",
        body,
      }),
    testEndpoint: (reportId: string, body: ExternalEndpointConfig) =>
      apiFetch<{ ok: boolean; status?: number; message: string; sampleData?: unknown }>(
        `/v1/admin/reports/${reportId}/endpoint/test`,
        { method: "POST", body }
      ),

    listTemplateVersions: (reportId: string) =>
      apiFetch<ReportTemplateVersion[]>(`/v1/admin/reports/${reportId}/templates`),
    getTemplateVersion: (reportId: string, versionId: string) =>
      apiFetch<ReportTemplateVersion>(`/v1/admin/reports/${reportId}/templates/${versionId}`),
    saveDraftTemplate: (
      reportId: string,
      body: { htmlContent: string; sampleDataJson: string; changeNote?: string }
    ) =>
      apiFetch<ReportTemplateVersion>(`/v1/admin/reports/${reportId}/templates`, {
        method: "POST",
        body,
      }),
    updateDraftTemplate: (
      reportId: string,
      versionId: string,
      body: { htmlContent: string; sampleDataJson: string }
    ) =>
      apiFetch<ReportTemplateVersion>(`/v1/admin/reports/${reportId}/templates/${versionId}`, {
        method: "PUT",
        body,
      }),
    publishTemplate: (reportId: string, versionId: string) =>
      apiFetch<ReportTemplateVersion>(
        `/v1/admin/reports/${reportId}/templates/${versionId}/publish`,
        { method: "POST" }
      ),
    deleteTemplateVersion: (reportId: string, versionId: string) =>
      apiFetch<void>(`/v1/admin/reports/${reportId}/templates/${versionId}`, { method: "DELETE" }),
    previewTemplate: (
      reportId: string,
      body: {
        htmlContent: string;
        sampleDataJson: string;
        useLiveData?: boolean;
        params?: Record<string, unknown>;
      }
    ) =>
      apiFetch<{ renderedHtml: string }>(`/v1/admin/reports/${reportId}/templates/preview`, {
        method: "POST",
        body,
      }),
    exportTemplate: (
      reportId: string,
      body: {
        htmlContent: string;
        sampleDataJson: string;
        format: string;
        pageLayout?: string;
        pageWidth?: number | null;
        pageHeight?: number | null;
        useLiveData?: boolean;
        params?: Record<string, unknown>;
      }
    ) => apiDownload(`/v1/admin/reports/${reportId}/templates/export`, body),
  },

  executionLogs: {
    list: async (params?: { reportDefinitionId?: string; page?: number; size?: number }) => {
      const qs = new URLSearchParams();
      if (params?.reportDefinitionId) qs.set("reportDefinitionId", params.reportDefinitionId);
      if (params?.page !== undefined) qs.set("page", String(params.page));
      if (params?.size !== undefined) qs.set("size", String(params.size));
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const result = await apiFetch<Page<ReportExecutionLog>>(`/v1/admin/execution-logs${suffix}`);
      return result.content;
    },
  },

  branding: {
    get: () => apiFetch<BrandingDto>("/v1/admin/branding"),
    save: (body: BrandingRequest) =>
      apiFetch<BrandingDto>("/v1/admin/branding", { method: "PUT", body }),
  },

  emailConfig: {
    get: () => apiFetch<EmailConfigDto | null>("/v1/admin/email-config"),
    save: (body: EmailConfigRequest) =>
      apiFetch<EmailConfigDto>("/v1/admin/email-config", { method: "PUT", body }),
    test: (to: string) =>
      apiFetch<{ ok: boolean; message: string }>("/v1/admin/email-config/test", {
        method: "POST",
        body: { to },
      }),
  },

  schedules: {
    list: () => apiFetch<ReportSchedule[]>("/v1/admin/schedules"),
    get: (id: string) => apiFetch<ReportSchedule>(`/v1/admin/schedules/${id}`),
    create: (body: ReportScheduleRequest) =>
      apiFetch<ReportSchedule>("/v1/admin/schedules", { method: "POST", body }),
    update: (id: string, body: ReportScheduleRequest) =>
      apiFetch<ReportSchedule>(`/v1/admin/schedules/${id}`, { method: "PUT", body }),
    remove: (id: string) => apiFetch<void>(`/v1/admin/schedules/${id}`, { method: "DELETE" }),
    enable: (id: string) =>
      apiFetch<ReportSchedule>(`/v1/admin/schedules/${id}/enable`, { method: "POST" }),
    disable: (id: string) =>
      apiFetch<ReportSchedule>(`/v1/admin/schedules/${id}/disable`, { method: "POST" }),
    runNow: (id: string) =>
      apiFetch<void>(`/v1/admin/schedules/${id}/run-now`, { method: "POST" }),
    runs: async (id: string, limit = 20) => {
      const result = await apiFetch<Page<ReportScheduleRun>>(`/v1/admin/schedules/${id}/runs?limit=${limit}`);
      return result.content;
    },
  },

  billing: {
    plans: () => apiFetch<Plan[]>("/v1/billing/plans"),
    subscription: () => apiFetch<Subscription>("/v1/billing/subscription"),
    subscribe: (body: SubscribeRequest) =>
      apiFetch<SubscribeResponse>("/v1/billing/subscribe", { method: "POST", body }),
    payments: (limit = 20) =>
      apiFetch<Page<PaymentTransaction>>(`/v1/billing/payments?limit=${limit}`),
  },

  platform: {
    stats: () => apiFetch<PlatformStats>("/v1/admin/platform/stats"),
    tenants: () => apiFetch<TenantUsage[]>("/v1/admin/platform/tenants"),

    mpesaConfig: {
      get: () => apiFetch<MpesaConfigDto>("/v1/admin/platform/mpesa-config"),
      save: (body: MpesaConfigRequest) =>
        apiFetch<MpesaConfigDto>("/v1/admin/platform/mpesa-config", { method: "PUT", body }),
    },
    revenue: () => apiFetch<RevenueOverview>("/v1/admin/platform/revenue"),
    payments: (limit = 50) =>
      apiFetch<Page<PaymentTransaction>>(`/v1/admin/platform/payments?limit=${limit}`),
    recordManualPayment: (tenantId: string, body: ManualPaymentRequest) =>
      apiFetch<PaymentTransaction>(`/v1/admin/platform/tenants/${tenantId}/manual-payment`, {
        method: "POST",
        body,
      }),

    emailConfig: {
      get: () => apiFetch<EmailConfigDto | null>("/v1/admin/platform/email-config"),
      save: (body: EmailConfigRequest) =>
        apiFetch<EmailConfigDto>("/v1/admin/platform/email-config", { method: "PUT", body }),
      test: (to: string) =>
        apiFetch<{ ok: boolean; message: string }>("/v1/admin/platform/email-config/test", {
          method: "POST",
          body: { to },
        }),
    },

    licenses: {
      list: () => apiFetch<SelfHostLicense[]>("/v1/admin/platform/licenses"),
      issue: (licenseeLabel: string, validDays: number, installationId?: string) =>
        apiFetch<IssuedLicense>("/v1/admin/platform/licenses", {
          method: "POST",
          body: { licenseeLabel, validDays, installationId: installationId?.trim() || null },
        }),
      revoke: (id: string) =>
        apiFetch<void>(`/v1/admin/platform/licenses/${id}/revoke`, { method: "POST" }),
    },
  },
};
